import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { apiBase, makeApi } from "./api.js";
import { authConfigured, cognito, googleSignInArgs, signOutUrl } from "./auth.js";
import { Confirm } from "./pages/Confirm.js";
import { Recurring } from "./pages/Recurring.js";
import { Statements } from "./pages/Statements.js";

interface Health {
  status: string;
}

export { apiBase };

function Landing({ onSignIn, onGoogle }: { onSignIn?: () => void; onGoogle?: () => void }) {
  const [health, setHealth] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetch(`${apiBase}/health`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setHealth)
      .catch((e: Error) => setError(e.message));
  }, []);
  return (
    <section>
      <p>Find recurring charges, decide what to cut, and get a copy-paste cancellation playbook.</p>
      {onSignIn && onGoogle ? (
        <p className="sign-in">
          <button type="button" onClick={onGoogle} data-testid="sign-in-google">
            Continue with Google
          </button>{" "}
          <button type="button" className="link" onClick={onSignIn} data-testid="sign-in">
            Use email instead
          </button>
        </p>
      ) : onSignIn ? (
        <button type="button" onClick={onSignIn} data-testid="sign-in">
          Sign in
        </button>
      ) : (
        <p className="error">Sign-in is not configured for this build.</p>
      )}
      <p data-testid="api-status">
        API: {health ? health.status : error ? `unreachable (${error})` : "checking…"}
      </p>
    </section>
  );
}

export function App() {
  const auth = useAuth();
  const signedIn = authConfigured && auth.isAuthenticated;
  const api = useMemo(() => makeApi(() => auth.user?.id_token), [auth.user?.id_token]);

  return (
    <main>
      <header>
        <h1>
          <Link to="/">Paredown</Link>
        </h1>
        {signedIn && (
          <nav>
            <Link to="/statements">Statements</Link>
            <Link to="/recurring">Recurring charges</Link>
            <a href={signOutUrl()} onClick={() => void auth.removeUser()}>
              Sign out
            </a>
          </nav>
        )}
      </header>
      {auth.error && <p className="error">Sign-in failed: {auth.error.message}</p>}
      {auth.isLoading ? (
        <p>Signing in…</p>
      ) : !signedIn ? (
        <Landing
          onSignIn={authConfigured ? () => void auth.signinRedirect() : undefined}
          onGoogle={authConfigured && cognito.googleSignIn ? () => void auth.signinRedirect(googleSignInArgs) : undefined}
        />
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/statements" replace />} />
          <Route path="/statements" element={<Statements api={api} />} />
          <Route path="/statements/:id" element={<Confirm api={api} />} />
          <Route path="/recurring" element={<Recurring api={api} accountEmail={auth.user?.profile.email} />} />
          <Route path="*" element={<Navigate to="/statements" replace />} />
        </Routes>
      )}
    </main>
  );
}
